import { useState } from 'react';
import type { TaskAgent } from '../../../types/tabs';

interface PlanStepCardProps {
  step: TaskAgent & { index: number }; // TaskAgent with computed index
  onDelete: () => void;
  isDeleting?: boolean;
  onUpdateDescription?: (stepIndex: number, newDescription: string) => Promise<boolean>;
}

export default function PlanStepCard({ step, onDelete, isDeleting, onUpdateDescription }: PlanStepCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(step.description);
  const [isSaving, setIsSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // collapsed by default per user preference

  const handleEdit = () => {
    setIsEditing(true);
    setEditedDescription(step.description);
  };

  const handleSave = async () => {
    if (!onUpdateDescription) return;
    setIsSaving(true);
    const success = await onUpdateDescription(step.index, editedDescription);
    setIsSaving(false);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedDescription(step.description);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const statusStyles = {
    pending: 'bg-gray-100 text-gray-600',
    in_progress: 'bg-blue-100 text-blue-700',
    awaiting_user: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };

  const statusLabels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    awaiting_user: 'Awaiting User',
    completed: 'Completed',
    failed: 'Failed',
  };

  const statusIcons = {
    pending: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" strokeWidth="2" />
      </svg>
    ),
    in_progress: (
      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    ),
    awaiting_user: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    completed: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
      </svg>
    ),
    failed: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow group">
      {/* Header - clickable to toggle */}
      <div
        onClick={toggleExpanded}
        className="flex items-center justify-between p-4 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          {/* Expand/collapse arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
            className="flex-shrink-0 w-5 h-5 hover:bg-gray-100 rounded flex items-center justify-center transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            <svg
              className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Step number */}
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
            {step.index + 1}
          </span>

          {/* Agent name */}
          <span className="font-medium text-gray-900">{step.leader_agent}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Delete button - for pending and awaiting_user steps */}
          {(step.status === 'pending' || step.status === 'awaiting_user') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isDeleting}
              className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete step"
            >
              {isDeleting ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          )}
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusStyles[step.status]}`}>
            {statusIcons[step.status]}
            {statusLabels[step.status]}
          </span>
        </div>
      </div>

      {/* Description - collapsible */}
      <div
        className={`overflow-hidden transition-all duration-300 border-t border-gray-100 ${
          isExpanded ? 'max-h-96' : 'max-h-0'
        }`}
      >
        {isEditing ? (
        <div className="p-4 pt-3">
          <textarea
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 p-4 pt-3">
          <p className="text-gray-600 text-sm leading-relaxed flex-1">{step.description}</p>
          {(step.status === 'pending' || step.status === 'awaiting_user') && onUpdateDescription && (
            <button
              onClick={handleEdit}
              className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200"
              title="Edit description"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
